import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  TextField,
  useScrollTrigger,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Link } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

// Test coordinates for development
const TEST_LOCATIONS = [
  { name: "PHL Airport", lat: 39.872940, lon: -75.243988 },
  { name: "NYC Times Square", lat: 40.758896, lon: -73.985130 },
  { name: "LAX Airport", lat: 33.942536, lon: -118.408075 },
  { name: "Marsh Creek Statae Park", lat: 40.066549, lon: -75.720308 },
  { name: "Random spot in West Chester", lat: 39.966185, lon: -75.526551 },
  { name: "Wayne train station", lat: 40.045388, lon: -75.386451 },

];

const SHOW_TEST_LOCATIONS =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_ENABLE_TEST_LOCATIONS === 'true';

export default function MainNavigation({
  onSettingsClick,
  userLat,
  userLon,
  address,
  setAddress,
  onFindMe,
  onHomeAddressSearch,
  onMapAddressCenter,
}) {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 2 });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Test coordinates dropdown state
  const [testMenuAnchor, setTestMenuAnchor] = useState(null);
  const testMenuOpen = Boolean(testMenuAnchor);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleTestMenuOpen = (event) => {
    event.stopPropagation();
    setTestMenuAnchor(event.currentTarget);
  };
  const handleTestMenuClose = () => setTestMenuAnchor(null);

  const handleTestLocationSelect = (location) => {
    const currentPath = window.location.pathname;
    setAddress?.(`${location.lat.toFixed(5)}, ${location.lon.toFixed(5)}`);
    if (currentPath === "/") onHomeAddressSearch?.(location.lat, location.lon);
    else if (currentPath === "/map") onMapAddressCenter?.(location.lat, location.lon);
    handleTestMenuClose();
  };

  const handleAddressSearch = async () => {
    const currentPath = window.location.pathname;

    // 1) "lat,lon"
    const coordMatch =
      address && address.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (coordMatch) {
      const latNum = parseFloat(coordMatch[1]);
      const lonNum = parseFloat(coordMatch[2]);
      setAddress?.(`${latNum.toFixed(5)}, ${lonNum.toFixed(5)}`);
      if (currentPath === "/") onHomeAddressSearch?.(latNum, lonNum);
      else if (currentPath === "/map") onMapAddressCenter?.(latNum, lonNum);
      return;
    }

    // 2) Empty input but coords exist
    if ((!address || address.trim() === "") && userLat != null && userLon != null) {
      setAddress?.(`${Number(userLat).toFixed(5)}, ${Number(userLon).toFixed(5)}`);
      if (currentPath === "/") onHomeAddressSearch?.(userLat, userLon);
      else if (currentPath === "/map") onMapAddressCenter?.(userLat, userLon);
      return;
    }

    // 3) Geocode address
    if (!address || !address.trim()) return;
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      address
    )}&limit=1`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const latNum = parseFloat(lat);
        const lonNum = parseFloat(lon);
        setAddress?.(`${latNum.toFixed(5)}, ${lonNum.toFixed(5)}`);
        if (currentPath === "/") onHomeAddressSearch?.(latNum, lonNum);
        else if (currentPath === "/map") onMapAddressCenter?.(latNum, lonNum);
      } else {
        alert("Address not found.");
      }
    } catch (err) {
      console.error("Geocoding error:", err);
    }
  };

  // ✅ Check login state via secure cookie
  useEffect(() => {
    fetch("/authapi/auth_check.php", {
      credentials: "include"
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.loggedIn) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
        }
      })
      .catch(() => setIsLoggedIn(false));
  }, []);

  return (
    <AppBar
      position="sticky"
      elevation={trigger ? 4 : 0}
      sx={{ top: 0, zIndex: 1200, transition: 'box-shadow .2s ease' }}
    >
      <Toolbar sx={{ gap: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          FlightIQ{' '}
          <span
            onClick={SHOW_TEST_LOCATIONS ? handleTestMenuOpen : undefined}
            style={{
              color: 'red',
              fontStyle: 'italic',
              cursor: SHOW_TEST_LOCATIONS ? 'pointer' : 'default',
              userSelect: 'none'
            }}
            title={SHOW_TEST_LOCATIONS ? 'Dev: Click for test locations' : undefined}
          >
            (Beta)
          </span>
        </Typography>

        {/* Search + GO */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flexGrow: 1,
            maxWidth: '600px',
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Enter address or lat,lon"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            sx={{ width: '50%', minWidth: 150 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="use current location"
                    onClick={() =>
                      onFindMe({ fillOnly: true }, (lat, lon) =>
                        setAddress(`${lat.toFixed(5)}, ${lon.toFixed(5)}`)
                      )
                    }
                    edge="end"
                  >
                    <MyLocationIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleAddressSearch} sx={{ flexShrink: 0 }}>
            GO
          </Button>
        </Box>

        {/* Nav links */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 2 }}>
          <Button color="inherit" component={Link} to="/">
            Home
          </Button>
          <Button color="inherit" component={Link} to="/map">
            Map
          </Button>
          <Button color="inherit" component={Link} to="/about">
            About
          </Button>
        </Box>

        <IconButton color="inherit" onClick={onSettingsClick} sx={{ ml: 2 }}>
          <SettingsIcon />
        </IconButton>

        {/* Account menu */}
         <IconButton color="inherit" onClick={handleMenuOpen}>
           <AccountCircle style={{ color: user ? "limegreen" : "white" }} />
         </IconButton>
<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
  {!user ? (
    <>
      <MenuItem component={Link} to="/login" onClick={handleMenuClose}>
        Login
      </MenuItem>
      <MenuItem component={Link} to="/register" onClick={handleMenuClose}>
        Register
      </MenuItem>
    </>
  ) : (
    <>
      <MenuItem disabled>Welcome, {user.username}</MenuItem>
      <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
        Profile
      </MenuItem>
      {/* ✅ Only show for admins */}
{user.role === "admin" && (
  <MenuItem component={Link} to="/sysop" onClick={handleMenuClose}>
    Admin
  </MenuItem>
)}
      <MenuItem
        onClick={() => {
          logout();
          handleMenuClose();
        }}
      >
        Logout
      </MenuItem>
    </>
  )}
</Menu>

        {/* Test Locations Menu (Hidden - Click Beta to open) */}
        {SHOW_TEST_LOCATIONS && (
          <Menu
            anchorEl={testMenuAnchor}
            open={testMenuOpen}
            onClose={handleTestMenuClose}
            PaperProps={{
              sx: {
                maxHeight: 400,
                backgroundColor: '#1e1e1e',
                border: '1px solid #444',
              }
            }}
          >
            <MenuItem disabled sx={{ fontWeight: 'bold', color: '#fff' }}>
              Test Locations
            </MenuItem>
            {TEST_LOCATIONS.map((loc, idx) => (
              <MenuItem
                key={idx}
                onClick={() => handleTestLocationSelect(loc)}
                sx={{
                  fontSize: '0.9rem',
                  '&:hover': { backgroundColor: '#333' }
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {loc.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    {loc.lat.toFixed(5)}, {loc.lon.toFixed(5)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        )}
      </Toolbar>
    </AppBar>
  );
}
